import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type QueryRequest } from '@vello/analytics-semantic';
import {
  type BiErrorView,
  type ExplorerState,
  type ImportOutcome,
  type ReportStorage,
  type SavedReport,
  BI_SAVE_POLICY,
  biErrorView,
  grantsAllow,
  importLegacyReports,
  readLegacyReports,
  reportErrorView,
  savedReportView,
} from '../../../src/index';
import { useAdmin } from '../../session';
import { type Loadable } from './bi-hooks';

/**
 * The operator's saved reports (D523): kept on the SERVER, per operator (`/v1/analytics/reports`). A report is a name and a query, never
 * an answer. The first time the list opens, whatever D522 left in this browser's `localStorage` is offered to the server once and then
 * cleared (`importLegacyReports`); every browser access is wrapped, so a blocked store simply means there is nothing to import.
 */
function browserStorage(): ReportStorage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export type ReportOutcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: BiErrorView };

export interface ReportsApi {
  readonly state: Loadable<readonly SavedReport[]>;
  /** The operator holds `analytics:save_report`: the screen offers to save, rename and delete. */
  readonly canSave: boolean;
  /** What the one-time import of this browser's old reports did, once it ran (to say so on screen). */
  readonly imported: ImportOutcome | undefined;
  readonly reload: () => void;
  readonly add: (name: string, query: QueryRequest) => Promise<ReportOutcome<SavedReport>>;
  readonly rename: (id: string, name: string) => Promise<ReportOutcome<SavedReport>>;
  readonly remove: (id: string) => Promise<ReportOutcome<undefined>>;
}

export function useSavedReports(): ReportsApi {
  const { runtime, me, grants } = useAdmin();
  const owner = me?.id ?? 'anonymous';
  const canSave = grants !== undefined && grantsAllow(grants, BI_SAVE_POLICY);
  const storage = useMemo(browserStorage, []);
  const [state, setState] = useState<Loadable<readonly SavedReport[]>>({ status: 'loading' });
  const [imported, setImported] = useState<ImportOutcome | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  /** The import is tried once per mount (it clears the browser's copy when it finishes); a failed one is tried again on the next open. */
  const importTried = useRef(false);
  const api = runtime.analyticsBi;

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void (async () => {
      const listed = await api.reports();
      if (!live) return;
      if (!listed.ok) {
        setState({ status: 'error', error: biErrorView(listed.error) });
        return;
      }
      let reports = listed.value.reports.map(savedReportView);
      setState({ status: 'ready', value: reports });
      if (!canSave || importTried.current || readLegacyReports(storage, owner).length === 0) return;
      importTried.current = true;
      const outcome = await importLegacyReports({
        storage,
        ownerId: owner,
        existing: reports.map((r) => r.name),
        save: async (name, query) => {
          const res = await api.saveReport({ name, query });
          return res.ok ? { ok: true } : { ok: false, error: res.error };
        },
      });
      if (!live) return;
      setImported(outcome);
      if (outcome.imported > 0) {
        const again = await api.reports();
        if (live && again.ok) {
          reports = again.value.reports.map(savedReportView);
          setState({ status: 'ready', value: reports });
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [api, attempt, canSave, owner, storage]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const upsert = useCallback((report: SavedReport): void => {
    setState((s) => {
      if (s.status !== 'ready') return s;
      const rest = s.value.filter((r) => r.id !== report.id);
      return { status: 'ready', value: [report, ...rest] };
    });
  }, []);

  const add = useCallback<ReportsApi['add']>(
    async (name, query) => {
      const res = await api.saveReport({ name, query });
      if (!res.ok) return { ok: false, error: reportErrorView(res.error) };
      const report = savedReportView(res.value.report);
      upsert(report);
      return { ok: true, value: report };
    },
    [api, upsert],
  );
  const rename = useCallback<ReportsApi['rename']>(
    async (id, name) => {
      const res = await api.updateReport(id, { name });
      if (!res.ok) return { ok: false, error: reportErrorView(res.error) };
      const report = savedReportView(res.value.report);
      upsert(report);
      return { ok: true, value: report };
    },
    [api, upsert],
  );
  const remove = useCallback<ReportsApi['remove']>(
    async (id) => {
      const res = await api.deleteReport(id);
      // A report that is already gone (deleted in another tab) is gone: the list just drops it.
      if (!res.ok && res.error.reasonCode !== 'ANALYTICS_REPORT_NOT_FOUND') return { ok: false, error: reportErrorView(res.error) };
      setState((s) => (s.status === 'ready' ? { status: 'ready', value: s.value.filter((r) => r.id !== id) } : s));
      return { ok: true, value: undefined };
    },
    [api],
  );

  return { state, canSave, imported, reload, add, rename, remove };
}

/** A hand-off from "Relatórios salvos" to the explorer: the report to open, consumed once. */
let pendingReport: ExplorerState | undefined;
export function openInExplorer(state: ExplorerState): void {
  pendingReport = state;
}
export function takePendingReport(): ExplorerState | undefined {
  const s = pendingReport;
  pendingReport = undefined;
  return s;
}
